import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaRupeeSign,
  FaCalendarCheck,
  FaUsers,
  FaCut,
  FaChartLine,
  FaClock,
  FaFileInvoice,
} from 'react-icons/fa';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import '../Css/Dashboard.css';
import { baseurl } from '../Baseurl';

const STATUS_COLORS = {
  upcoming: '#1e63b8',
  progress: '#3892e0',
  completed: '#14498c',
  walkin: '#2f7fd6',
  cancelled: '#0b2f5c',
};

const CATEGORY_COLORS = ['#0a2647', '#14498c', '#1e63b8', '#2f7fd6', '#3892e0'];


function Dashboard() {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));

    const fetchDashboard = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const res = await axios.get(`${baseurl}/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setData(res.data.data);
      } catch (err) {
        console.log('Dashboard fetch error:', err.response?.data || err.message);
        setErrorMsg(err.response?.data?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (errorMsg) {
    return <div className="dashboard-error">{errorMsg}</div>;
  }

  const revenueTrendData = data.revenueTrend.map((r) => ({
    date: new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    revenue: Number(r.revenue),
  }));

  const statusPieData = data.appointmentStatusBreakdown.map((s) => ({
    name: s.status,
    value: s.count,
  }));

  const categoryBarData = data.categoryBreakdown.map((c) => ({
    category: c.category,
    count: c.count,
  }));

  const paymentPieData = data.paymentMethodBreakdown.map((p) => ({
    name: p.method.toUpperCase(),
    value: Number(p.total),
  }));

  return (
    <div className="dashboard-page">
      {/* HEADER */}
      <div className="dashboard-header">
        <div>
          <h2>Welcome back{user?.name ? `, ${user.name}` : ''} 👋</h2>
          <p>Here's what's happening at your salon today</p>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="dash-stats-grid">
        <div className="dash-stat-card">
          <div className="dash-stat-icon revenue">
            <FaRupeeSign />
          </div>
          <div>
            <span className="dash-stat-value">₹{data.todayRevenue.toLocaleString()}</span>
            <span className="dash-stat-label">Today's Revenue</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon month">
            <FaChartLine />
          </div>
          <div>
            <span className="dash-stat-value">₹{data.monthRevenue.toLocaleString()}</span>
            <span className="dash-stat-label">This Month</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon appt">
            <FaCalendarCheck />
          </div>
          <div>
            <span className="dash-stat-value">{data.todayAppointments}</span>
            <span className="dash-stat-label">Today's Appointments</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon clients">
            <FaUsers />
          </div>
          <div>
            <span className="dash-stat-value">{data.totalClients}</span>
            <span className="dash-stat-label">Total Clients</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon staff">
            <FaUsers />
          </div>
          <div>
            <span className="dash-stat-value">{data.activeStaffCount}</span>
            <span className="dash-stat-label">Active Staff</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon services">
            <FaCut />
          </div>
          <div>
            <span className="dash-stat-value">{data.activeServicesCount}</span>
            <span className="dash-stat-label">Active Services</span>
          </div>
        </div>
      </div>

      {/* CHARTS ROW 1 */}
      <div className="dash-charts-row">
        <div className="dash-chart-card wide">
          <div className="dash-chart-head">
            <h4>Revenue Trend (Last 7 Days)</h4>
          </div>
          {revenueTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenueTrendData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#123a34" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#123a34" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceeed" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(val) => `₹${val.toLocaleString()}`} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#123a34"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-empty">No revenue data yet</div>
          )}
        </div>

        <div className="dash-chart-card">
          <div className="dash-chart-head">
            <h4>Today's Appointment Status</h4>
          </div>
          {statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                >
                  {statusPieData.map((entry, index) => (
                    <Cell key={index} fill={STATUS_COLORS[entry.name] || '#888'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={30} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-empty">No appointments today</div>
          )}
        </div>
      </div>

      {/* CHARTS ROW 2 */}
      <div className="dash-charts-row">
        <div className="dash-chart-card">
          <div className="dash-chart-head">
            <h4>Top Services by Bookings</h4>
          </div>
          {data.topServices.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.topServices} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceeed" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="bookings" fill="#1e63b8" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-empty">No service bookings yet</div>
          )}
        </div>

        <div className="dash-chart-card">
          <div className="dash-chart-head">
            <h4>Appointments by Category</h4>
          </div>
          {categoryBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={categoryBarData}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  label
                >
                  {categoryBarData.map((entry, index) => (
                    <Cell key={index} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-empty">No category data yet</div>
          )}
        </div>

        <div className="dash-chart-card">
          <div className="dash-chart-head">
            <h4>Payments by Method</h4>
          </div>
          {paymentPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={paymentPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  label
                >
                  {paymentPieData.map((entry, index) => (
                    <Cell key={index} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `₹${val.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-empty">No payment data yet</div>
          )}
        </div>
      </div>

      {/* TABLES ROW */}
      <div className="dash-tables-row">
        <div className="dash-table-card">
          <div className="dash-chart-head">
            <h4>
              <FaClock /> Today's Appointments
            </h4>
          </div>
          <table className="dash-mini-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Service</th>
                <th>Staff</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recentAppointments.map((a) => (
                <tr key={a.id}>
                  <td>{a.client_name}</td>
                  <td>{a.service_name}</td>
                  <td>{a.staff_name}</td>
                  <td>{a.appointment_time}</td>
                  <td>
                    <span
                      className="dash-status-pill"
                      style={{
                        background: `${STATUS_COLORS[a.status]}1a`,
                        color: STATUS_COLORS[a.status],
                      }}
                    >
                      {a.status}
                    </span>
                  </td>
                </tr>
              ))}
              {data.recentAppointments.length === 0 && (
                <tr>
                  <td colSpan="5" className="dash-empty-row">
                    No appointments scheduled today
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="dash-table-card">
          <div className="dash-chart-head">
            <h4>
              <FaFileInvoice /> Recent Invoices
            </h4>
          </div>
          <table className="dash-mini-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recentInvoices.map((inv) => (
                <tr key={inv.invoice_number}>
                  <td>{inv.invoice_number}</td>
                  <td>{inv.client_name}</td>
                  <td>₹{Number(inv.grand_total).toLocaleString()}</td>
                  <td>
                    <span
                      className={`dash-status-pill ${
                        inv.status === 'paid'
                          ? 'pill-paid'
                          : inv.status === 'partial'
                          ? 'pill-partial'
                          : 'pill-due'
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
              {data.recentInvoices.length === 0 && (
                <tr>
                  <td colSpan="4" className="dash-empty-row">
                    No invoices yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;